import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Maximize2, Minus, Plus, Trash2 } from 'react-feather';
import { adjuntoDentroDeLimite } from '../../features/tickets';

// Tamaño mínimo al reducir y paso de los botones Reducir / Ampliar.
const ANCHO_MIN = 48;
const FACTOR = 1.25;

const AVISO_LIMITE = 'Alguna imagen supera el límite de 3,7 MB y se descartó.';

// innerText respeta los saltos de línea de los bloques; jsdom no lo implementa.
const textoDe = (el) => String(el.innerText ?? el.textContent ?? '').trim();

const leerComoDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => resolve(String(lector.result));
    lector.onerror = () => reject(lector.error);
    lector.readAsDataURL(file);
  });

const soloImagenes = (lista) => Array.from(lista || []).filter((f) => f.type.startsWith('image/'));
const sinImagenes = (lista) => Array.from(lista || []).filter((f) => !f.type.startsWith('image/'));

const ESQUINAS = [
  { id: 'nw', x: 'izq', y: 'arr', lado: -1, cursor: 'nwse-resize' },
  { id: 'ne', x: 'der', y: 'arr', lado: 1, cursor: 'nesw-resize' },
  { id: 'sw', x: 'izq', y: 'aba', lado: -1, cursor: 'nesw-resize' },
  { id: 'se', x: 'der', y: 'aba', lado: 1, cursor: 'nwse-resize' },
];

/**
 * Caja de respuesta del ticket: texto plano en el que además se pueden PEGAR o SOLTAR imágenes
 * (capturas de pantalla), que se ven dentro del propio texto. Lo pegado como texto entra siempre
 * como texto plano, sin formato ajeno.
 *
 * Es una caja sin estado de React: el contenido vive en el DOM y se lee con `leer()`.
 *  - leer()  → { texto, html, imagenes }. `html` solo viene si hay imágenes; cada una sale de
 *              data: URL a `cid:<cid>` y se devuelve en `imagenes` lista para enviar como adjunto.
 *  - limpiar() / fijarTexto(t) / insertarTexto(t) / enfocar()
 *
 * Las imágenes se redimensionan (como en HS): al pulsar una aparece su barra (Reducir, Ampliar,
 * Ajustar al ancho, Quitar) y un tirador en la esquina para arrastrar. El ancho que se ve es el que
 * sale en el correo (`width` de la imagen en el HTML).
 */
const RespuestaEditor = forwardRef(
  ({ etiqueta, placeholder = '', nota = false, onTexto, onError, onArchivos }, ref) => {
    const cajaRef = useRef(null);
    const envoltorioRef = useRef(null);
    const imgSelRef = useRef(null);
    // Imagen seleccionada y dónde está su esquina inferior derecha (para el tirador).
    const [medida, setMedida] = useState(null);

    const medir = (img) => {
      const env = envoltorioRef.current.getBoundingClientRect();
      const r = img.getBoundingClientRect();
      setMedida({
        izq: r.left - env.left,
        der: r.right - env.left,
        arr: r.top - env.top,
        aba: r.bottom - env.top,
        ancho: Math.round(r.width),
      });
    };

    const seleccionar = (img) => {
      if (imgSelRef.current) imgSelRef.current.classList.remove('is-seleccionada');
      imgSelRef.current = img;
      if (!img) {
        setMedida(null);
        return;
      }
      img.classList.add('is-seleccionada');
      medir(img);
    };

    const anchoActual = (img) =>
      Math.round(img.getBoundingClientRect().width) || parseInt(img.style.width, 10) || 200;

    const fijarAncho = (img, px) => {
      const tope = cajaRef.current.clientWidth ? cajaRef.current.clientWidth - 26 : Infinity;
      img.style.width = `${Math.max(ANCHO_MIN, Math.min(Math.round(px), tope))}px`;
      img.style.height = 'auto';
      medir(img);
    };

    const ajustarAlAncho = (img) => fijarAncho(img, cajaRef.current.clientWidth - 26 || 600);

    const quitarImagen = (img) => {
      seleccionar(null);
      img.remove();
      if (onTexto) onTexto(textoDe(cajaRef.current));
    };

    // `lado` = -1 en las esquinas izquierdas: arrastrar hacia la izquierda ensancha.
    const empezarArrastre = (e, lado) => {
      e.preventDefault();
      const img = imgSelRef.current;
      if (!img) return;
      const x0 = e.clientX;
      const w0 = anchoActual(img);
      const mover = (ev) => fijarAncho(img, w0 + lado * (ev.clientX - x0));
      const soltar = () => {
        document.removeEventListener('mousemove', mover);
        document.removeEventListener('mouseup', soltar);
      };
      document.addEventListener('mousemove', mover);
      document.addEventListener('mouseup', soltar);
    };

    const avisarTexto = () => {
      if (onTexto) onTexto(textoDe(cajaRef.current));
      // Al escribir, la imagen puede moverse (o haberse borrado con Supr).
      const img = imgSelRef.current;
      if (img) {
        if (img.isConnected) medir(img);
        else seleccionar(null);
      }
    };

    // Inserta un nodo en el cursor; si el cursor no está dentro de la caja, al final.
    const insertarNodo = (nodo) => {
      const caja = cajaRef.current;
      const sel = window.getSelection();
      let rango = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
      if (!rango || !caja.contains(rango.commonAncestorContainer)) {
        rango = document.createRange();
        rango.selectNodeContents(caja);
        rango.collapse(false);
      }
      rango.deleteContents();
      rango.insertNode(nodo);
      rango.setStartAfter(nodo);
      rango.collapse(true);
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(rango);
      }
      avisarTexto();
    };

    const insertarImagenes = async (archivos) => {
      const validos = archivos.filter(adjuntoDentroDeLimite);
      if (validos.length < archivos.length && onError) onError(AVISO_LIMITE);
      // En orden: si se pegan varias, quedan en el mismo orden en que se pegaron.
      await validos.reduce(
        (previa, file) =>
          previa.then(async () => {
            try {
              const url = await leerComoDataUrl(file);
              const img = document.createElement('img');
              img.src = url;
              img.alt = '';
              img.style.maxWidth = '100%';
              insertarNodo(img);
            } catch {
              if (onError) onError('No se pudo leer la imagen.');
            }
          }),
        Promise.resolve(),
      );
    };

    const insertarTexto = (texto) => {
      const caja = cajaRef.current;
      caja.focus();
      if (
        typeof document.execCommand === 'function' &&
        document.execCommand('insertText', false, texto)
      ) {
        avisarTexto();
        return;
      }
      insertarNodo(document.createTextNode(texto));
    };

    const alPegar = (e) => {
      const otros = sinImagenes(e.clipboardData?.files);
      if (otros.length && onArchivos) onArchivos(otros);
      const imagenes = soloImagenes(e.clipboardData?.files);
      if (imagenes.length) {
        e.preventDefault();
        insertarImagenes(imagenes);
        return;
      }
      const texto = e.clipboardData?.getData('text/plain');
      if (texto) {
        e.preventDefault();
        insertarTexto(texto);
      }
    };

    const alSoltar = (e) => {
      const imagenes = soloImagenes(e.dataTransfer?.files);
      const otros = sinImagenes(e.dataTransfer?.files);
      if (!imagenes.length && !otros.length) return;
      e.preventDefault();
      if (otros.length && onArchivos) onArchivos(otros);
      if (imagenes.length) insertarImagenes(imagenes);
    };

    useImperativeHandle(ref, () => ({
      leer: () => {
        const caja = cajaRef.current;
        const clon = caja.cloneNode(true);
        const reales = Array.from(caja.querySelectorAll('img'));
        const imagenes = [];
        clon.querySelectorAll('img').forEach((img, i) => {
          const src = img.getAttribute('src') || '';
          const coma = src.indexOf(',');
          const cabecera = coma > 0 ? src.slice(0, coma) : '';
          if (!src.startsWith('data:image/') || !cabecera.endsWith(';base64')) {
            img.remove();
            return;
          }
          const tipo = cabecera.slice('data:'.length, -';base64'.length);
          const ext = tipo.split('/')[1].split('+')[0].replace('jpeg', 'jpg');
          const n = imagenes.length + 1;
          const cid = `img-${n}`;
          imagenes.push({
            cid,
            nombre: `imagen-${n}.${ext}`,
            tipo_mime: tipo,
            contenido_base64: src.slice(coma + 1),
          });
          img.setAttribute('src', `cid:${cid}`);
          // El tamaño que ve el agente es el que verá quien lo reciba.
          const real = reales[i];
          const px = real.style.width.endsWith('px')
            ? parseInt(real.style.width, 10)
            : Math.round(real.getBoundingClientRect().width);
          if (px > 0) img.setAttribute('width', String(px));
          img.removeAttribute('class');
          img.style.width = '';
          img.style.height = '';
        });
        return { texto: textoDe(caja), html: imagenes.length ? clon.innerHTML : null, imagenes };
      },
      limpiar: () => {
        seleccionar(null);
        cajaRef.current.innerHTML = '';
      },
      fijarTexto: (texto) => {
        cajaRef.current.textContent = texto;
      },
      insertarTexto,
      enfocar: () => cajaRef.current.focus(),
    }));

    // Los botones no deben quitarle el foco ni la selección a la caja.
    const sinFoco = (e) => e.preventDefault();

    return (
      <div className="tickets-respuesta-wrap" ref={envoltorioRef}>
        <div
          ref={cajaRef}
          className={`tickets-textarea tickets-respuesta${nota ? ' tickets-textarea--nota' : ''}`}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          tabIndex={0}
          aria-multiline="true"
          aria-label={etiqueta}
          data-placeholder={placeholder}
          onInput={avisarTexto}
          onPaste={alPegar}
          onDrop={alSoltar}
          onDragOver={(e) => e.preventDefault()}
          onClick={(e) => seleccionar(e.target instanceof HTMLImageElement ? e.target : null)}
          onKeyDown={(e) => e.key === 'Escape' && seleccionar(null)}
          onScroll={() => imgSelRef.current && medir(imgSelRef.current)}
          spellCheck
        />
        {medida && (
          <>
            {ESQUINAS.map((c) => (
              <span
                key={c.id}
                className="tickets-respuesta__asa"
                style={{ left: medida[c.x], top: medida[c.y], cursor: c.cursor }}
                onMouseDown={(e) => empezarArrastre(e, c.lado)}
                role="presentation"
                title="Arrastra para cambiar el tamaño"
              />
            ))}
            <div className="tickets-respuesta__barra" role="toolbar" aria-label="Imagen">
              <button
                type="button"
                className="tickets-respuesta__btn"
                aria-label="Reducir la imagen"
                title="Reducir la imagen"
                onMouseDown={sinFoco}
                onClick={() =>
                  fijarAncho(imgSelRef.current, anchoActual(imgSelRef.current) / FACTOR)
                }
              >
                <Minus size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="tickets-respuesta__btn"
                aria-label="Ampliar la imagen"
                title="Ampliar la imagen"
                onMouseDown={sinFoco}
                onClick={() =>
                  fijarAncho(imgSelRef.current, anchoActual(imgSelRef.current) * FACTOR)
                }
              >
                <Plus size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="tickets-respuesta__btn"
                aria-label="Ajustar la imagen al ancho"
                title="Ajustar la imagen al ancho"
                onMouseDown={sinFoco}
                onClick={() => ajustarAlAncho(imgSelRef.current)}
              >
                <Maximize2 size={14} aria-hidden="true" />
              </button>
              <span className="tickets-respuesta__medida">{medida.ancho} px</span>
              <button
                type="button"
                className="tickets-respuesta__btn"
                aria-label="Quitar la imagen"
                title="Quitar la imagen"
                onMouseDown={sinFoco}
                onClick={() => quitarImagen(imgSelRef.current)}
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </div>
          </>
        )}
      </div>
    );
  },
);

RespuestaEditor.displayName = 'RespuestaEditor';

RespuestaEditor.propTypes = {
  etiqueta: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  nota: PropTypes.bool,
  onTexto: PropTypes.func,
  onError: PropTypes.func,
  onArchivos: PropTypes.func,
};

export default RespuestaEditor;
